import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Ticket, X, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { API } from "@/lib/constants";

const LABELS = {
  en: { title: "Redeem promo code", sub: "Have a code? Enter it here to unlock Premium.", placeholder: "BETA2026", btn: "Redeem", success: "Premium unlocked! Enjoy 🎉", close: "Close" },
  fr: { title: "Utiliser un code promo", sub: "Tu as un code ? Entre-le pour débloquer le Premium.", placeholder: "BETA2026", btn: "Valider", success: "Premium débloqué ! Profites-en 🎉", close: "Fermer" },
  de: { title: "Promo-Code einlösen", sub: "Hast du einen Code? Gib ihn hier ein, um Premium freizuschalten.", placeholder: "BETA2026", btn: "Einlösen", success: "Premium freigeschaltet! 🎉", close: "Schließen" },
  it: { title: "Riscatta codice promo", sub: "Hai un codice? Inseriscilo qui per sbloccare il Premium.", placeholder: "BETA2026", btn: "Riscatta", success: "Premium sbloccato! 🎉", close: "Chiudi" },
  es: { title: "Canjear código promo", sub: "¿Tienes un código? Introdúcelo aquí para desbloquear el Premium.", placeholder: "BETA2026", btn: "Canjear", success: "¡Premium desbloqueado! 🎉", close: "Cerrar" },
  pt: { title: "Resgatar código promo", sub: "Tens um código? Insere-o aqui para desbloquear o Premium.", placeholder: "BETA2026", btn: "Resgatar", success: "Premium desbloqueado! 🎉", close: "Fechar" },
};

export default function RedeemPromoModal({ open, onClose, lang = "en", onSuccess }) {
  const t = LABELS[lang] || LABELS.en;
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API}/promo/redeem`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: code.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Failed");
      setOk(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="redeem-modal"
        >
          <motion.div
            className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl"
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-[#D95D39]/15 flex items-center justify-center">
                  <Ticket className="w-4 h-4 text-[#D95D39]" />
                </div>
                <div className="font-heading font-bold text-lg text-[#292524]">{t.title}</div>
              </div>
              <button onClick={onClose} className="text-[#78716C] hover:text-[#292524]"><X className="w-5 h-5" /></button>
            </div>
            {ok ? (
              <div className="text-center py-6">
                <CheckCircle2 className="w-14 h-14 text-[#3B8268] mx-auto mb-3" />
                <div className="font-heading font-bold text-lg text-[#292524] mb-4">{t.success}</div>
                <button onClick={onClose} className="btn-primary w-full">{t.close}</button>
              </div>
            ) : (
              <form onSubmit={submit}>
                <p className="text-sm text-[#57534E] mb-4">{t.sub}</p>
                <input
                  className="input mb-3 uppercase font-mono font-bold text-center text-lg tracking-wider"
                  placeholder={t.placeholder}
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  required
                  data-testid="redeem-code-input"
                />
                {error && (
                  <div className="text-sm text-[#D9423E] mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> {error}
                  </div>
                )}
                <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="redeem-submit-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Ticket className="w-4 h-4" /> {t.btn}</>}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
